import React, { HTMLAttributes, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { $task } from '../../../../store/states';
import Flex from '@components/Flex';
import { $authorizedUser, $permissions } from '@app/containers/store/states';
import { Button, Card, Intent } from '@blueprintjs/core';
import Heading from '@components/Heading';
import DevText from '@components/Text';
import { useTaskStateTransition } from '@pages/Task/hooks';
import { TaskState } from 'dy-frontend-http-repository/lib/data/enums';
import { TaskPermission } from 'dy-frontend-permissions/lib/permission';

export interface PreDeliveredProps {
    isStateTransitionAllowed: boolean;
}

export type Props = HTMLAttributes<HTMLDivElement> & PreDeliveredProps;

const PreDelivered: React.FC<Props> = (props) => {
    const permissions = useStore($permissions);
    const me = useStore($authorizedUser);
    const task = useStore($task);

    const { handleTransitionTaskState } = useTaskStateTransition();

    const [transitioningTo, setTransitionTo] = useState<TaskState | null>(null);

    const [isDeliveredAllowed, setIsDeliveredAllowed] = useState(false);

    useEffect(() => {
        if (!task || !me) {
            return;
        }
        setIsDeliveredAllowed(
            permissions.isRoot.task
            || permissions.has(TaskPermission.STATE_TRANSITION_DELIVERED),
        );
    }, [task, permissions, me]);

    if (!me) {
        return null;
    }

    if (!task) {
        // Task is NOT fetched yet
        return null;
    }

    // TODO: Render helper actions

    const handleTransitionToState = async (state: TaskState) => {
        try {
            setTransitionTo(state);
            const res = await handleTransitionTaskState(task.id, state);
        } catch (e) {
            // TODO: handle error
        } finally {
            setTransitionTo(null);
        }
    };

    const renderDeliveredTransitionButton = () => {
        if (!props.isStateTransitionAllowed || !isDeliveredAllowed) {
            return null;
        }

        return (
            <Button
                intent={Intent.PRIMARY}
                icon="saved"
                disabled={transitioningTo !== null}
                loading={transitioningTo === TaskState.DELIVERED}
                onClick={() => handleTransitionToState(TaskState.DELIVERED)}
            >Deliver and finalize request</Button>
        );
    };

    return (
        <Card>
            <Flex align="center" justify="space-between" className="mb-1">
                <Heading type="h4" className="mb-1">Pre-Delivered</Heading>
                <Flex>
                    <div>{renderDeliveredTransitionButton()}</div>
                </Flex>
            </Flex>

            <DevText muted>Congratulations! The client approved provided deliverables.</DevText>
            <DevText muted>
                Please upload the final deliverables and make sure you deliver the source files if requested.
            </DevText>
        </Card>
    );
};

export default PreDelivered;
